import { cn } from "@bowfast/ui/lib/utils";
import { GripVerticalIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import type { Waypoint } from "~/pages/routes/routes.types";

interface OverlayWaypointListProps {
  waypoints: Waypoint[];
}

export function OverlayWaypointList({ waypoints }: Readonly<OverlayWaypointListProps>) {
  return (
    <div>
      {waypoints.map((wp, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === waypoints.length - 1;
        const isMid = !isFirst && !isLast;

        return (
          <div key={wp.id} className="group relative flex items-center gap-3 py-2.5 pl-1">
            {!isLast && <div className="absolute left-[14px] top-[34px] bottom-0 w-px bg-white/15" />}
            <div
              className={cn(
                "relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full text-[0.6875rem] font-bold",
                wp.kind === "depart" && "bg-shallow-500 text-white",
                wp.kind === "arrive" && "bg-signal-500 text-white",
                wp.kind === "waypoint" && "border border-white/20 bg-white/10 text-white/80",
              )}
            >
              {wp.kind === "depart" ? "S" : wp.kind === "arrive" ? "E" : idx}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-tight text-white/90">{wp.name}</p>
              <p className="mt-0.5 flex gap-2 font-mono text-[0.625rem] text-white/40">
                <span>{wp.lat}</span>
                <span>{wp.lng}</span>
              </p>
            </div>
            {wp.distanceFromPrev != null && (
              <span className="shrink-0 font-mono text-[0.6875rem] tabular-nums text-white/50">
                {wp.distanceFromPrev.toLocaleString()} <span className="text-white/30">nm</span>
              </span>
            )}
            <div className="flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              {isMid && (
                <button
                  type="button"
                  className="flex size-6 items-center justify-center rounded-md text-white/40 hover:bg-white/10 hover:text-white/70 cursor-grab active:cursor-grabbing"
                  aria-label="Drag to reorder"
                >
                  <GripVerticalIcon className="size-3" />
                </button>
              )}
              <button
                type="button"
                className="flex size-6 items-center justify-center rounded-md text-white/40 hover:bg-white/10 hover:text-white/70"
                aria-label="Edit waypoint"
              >
                <PencilIcon className="size-3" />
              </button>
              {isMid && (
                <button
                  type="button"
                  className="flex size-6 items-center justify-center rounded-md text-white/40 hover:bg-coral-500/20 hover:text-coral-400"
                  aria-label="Remove waypoint"
                >
                  <Trash2Icon className="size-3" />
                </button>
              )}
            </div>
          </div>
        );
      })}
      <button
        type="button"
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-white/15 px-3 py-2 text-[0.8125rem] text-white/50 transition-colors hover:border-abyss-400/40 hover:bg-white/5 hover:text-white/70"
      >
        <PlusIcon className="size-3" />
        Add waypoint
      </button>
    </div>
  );
}
